import { PlantsGetResponse } from '@app/api/plants';
import { UsersCreateRequest } from '@app/api/users/UserCreateRequest';
import FormSelect from '@app/components/shared/FormSelect';
import yup from '@app/config/yup-locale';
import { useAuthenticationContext } from '@app/store/authentication/authenticationContext';
import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { AutocompleteChangeReason } from '@material-ui/lab/Autocomplete';
import React, { useState, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Roles, ROLES } from '../../userManagementUtils';
import FormButton from '@app/components/shared/FormButton';
import FormAutocompleteWithTags from '@app/components/shared/FormAutocompleteWithTags';
import WarningMessageComponent from '@app/components/shared/WarningMessageComponent';
import { SystemOfMeasurementOptions } from '@app/api/me/MeGetResponse';
import { useSystemOfMeasurementOptions } from '@app/hooks/useSystemOfMeasurementOptions';
import { useProfileFilter } from '@app/hooks/useProfileFilter';
import { DomainType } from '@app/api/users/ProfileFilterResponse';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import {
  validateMultipleDomains,
  getUserPlantsValue,
  isMultiPlantRole,
  calculateUserPlantsOnRoleChange,
  togglePlantSelection,
  handleLoginsChange,
  determineProfileFilterAction,
  filterRolesByDomainAndUser,
  shouldResetRoleSelection,
  getDomainValidationMessage,
  normalizeUserPlantsValue,
  extractPlantIdFromUserPlants
} from './inviteUserModalHelpers';
import { isValidGuid } from '@app/utils/guidHelpers';

// Restricted roles that plant admins cannot assign
const RESTRICTED_ROLES_FOR_PLANT_ADMIN = [Roles.Admin, Roles.KeyUser, Roles.TechnicalConsultant];

// RHI Magnesita allowed roles - derived from ROLES array to ensure consistency
// These are the role keys that RHI Magnesita users can invite without API validation
const RHI_MAGNESITA_ALLOWED_ROLE_KEYS = ROLES
  .filter(role => RESTRICTED_ROLES_FOR_PLANT_ADMIN.includes(role.value))
  .map(role => (role as any).roleKey as string);

interface EnhancedInviteUserModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (user: UsersCreateRequest) => void;
  allPlants: PlantsGetResponse[];
  isPlantAdmin?: boolean;
}

interface IFormInput {
  logins: string[];
  roleId: string;
  measurementSystem: SystemOfMeasurementOptions;
  userPlants: string[] | string;
}

const EnhancedInviteUserModal: React.FC<EnhancedInviteUserModalProps> = (props: EnhancedInviteUserModalProps) => {
  const { open, onClose, onConfirm, allPlants, isPlantAdmin } = props;
  const { t } = useTranslation();

  const { state: { userInfoObject } } = useAuthenticationContext();
  const userInfo = userInfoObject?.data;

  const { filterProfiles, clearFilter, setAsValidWithoutApiCall, isLoading, hasError, isValid, allowedProfiles, domainType } = useProfileFilter();
  const [domainValidationMessage, setDomainValidationMessage] = useState<string>('');
  const [multipleDomainError, setMultipleDomainError] = useState<string>('');

  const schema = yup.object().shape({
    logins: yup.array().of(yup.string().email().required()).min(1),
    roleId: yup.string().required(),
    measurementSystem: yup.string().required(),
    userPlants: isPlantAdmin
      ? yup.mixed()
      : yup.mixed().when("roleId", (roleId: string, schema) => {
          const isMultiPlantRole = [
            Roles.Admin.toString(),
            Roles.KeyUser.toString(),
            Roles.TechnicalConsultant.toString()
          ].includes(roleId.toString());

          return isMultiPlantRole
            ? schema.array().of(yup.string().required()).min(1)
            : schema.string().required();
        }),
  });

  const options = allPlants.map((plant) => ({ value: plant.id, name: plant.name, id: plant.id }));

  const initialPlantId = userInfo?.selectedPlant?.id;
  const hasValidInitialPlant = isValidGuid(initialPlantId);

  const {
    control,
    reset,
    watch,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors, isDirty, isValid: formIsValid },
  } = useForm<IFormInput>({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: 'all',
    defaultValues: {
      logins: [],
      roleId: '',
      measurementSystem: 'Metric' as SystemOfMeasurementOptions,
      userPlants: isPlantAdmin && hasValidInitialPlant ? [initialPlantId] : ''
    },
  });

  const watchRoleId = watch("roleId");
  const userPlants = watch("userPlants");
  const logins = watch("logins");

  // Extract the plantId from userPlants (can be string or string[])
  // When userPlants contains plant names (from FormAutocompleteWithTags), convert to IDs
  const selectedPlantId = useMemo(() =>
    extractPlantIdFromUserPlants(userPlants, allPlants),
    [userPlants, allPlants]
  );

  // Reset userPlants when role changes to match the expected type (array for multi-plant, string for single-plant)
  useEffect(() => {
    if (watchRoleId && !isPlantAdmin) {
      const currentValue = getValues('userPlants');
      const normalizationResult = normalizeUserPlantsValue(watchRoleId, currentValue);

      if (normalizationResult.shouldUpdate) {
        setValue('userPlants', normalizationResult.newValue!);
      }
    }
  }, [watchRoleId, isPlantAdmin, setValue, getValues]);

  // Filter roles based on domain validation and validate multiple domains
  useEffect(() => {
    // First, validate multiple domains
    const multiDomainValidation = validateMultipleDomains(logins, t);
    setMultipleDomainError(multiDomainValidation.errorMessage);

    // Determine what action to take based on validation state
    const filterAction = determineProfileFilterAction(
      multiDomainValidation.isValid,
      logins,
      selectedPlantId
    );

    if (filterAction.action === 'clear') {
      clearFilter();
      setDomainValidationMessage('');
    } else if (filterAction.action === 'filter') {
      filterProfiles({
        email: filterAction.email,
        plantId: filterAction.plantId
      });
    } else if (filterAction.action === 'skip') {
      // RHI Magnesita users can invite to any RHI role without backend validation
      // Roles are derived from ROLES array to maintain single source of truth
      setAsValidWithoutApiCall(DomainType.RhiMagnesita, RHI_MAGNESITA_ALLOWED_ROLE_KEYS);
      setDomainValidationMessage('');
    }
  }, [logins, selectedPlantId, filterProfiles, clearFilter, setAsValidWithoutApiCall, t]);

  // Memoize filtered roles to prevent infinite re-renders
  const filteredRoles = useMemo(() => {
    return filterRolesByDomainAndUser({
      multipleDomainError,
      logins,
      isPlantAdmin: isPlantAdmin ?? false,
      allowedProfiles,
      hasError,
      allRoles: ROLES,
      restrictedRoles: RESTRICTED_ROLES_FOR_PLANT_ADMIN
    });
  }, [allowedProfiles, hasError, isPlantAdmin, logins, multipleDomainError]);

  // Update domain validation message and reset role selection
  useEffect(() => {
    // Set domain validation message based on domain type
    const validationMessage = getDomainValidationMessage(
      isPlantAdmin ?? false,
      domainType,
      hasError,
      t
    );
    setDomainValidationMessage(validationMessage);

    // Reset role selection if currently selected role is not allowed
    const currentRoleId = getValues('roleId');
    if (shouldResetRoleSelection(currentRoleId, allowedProfiles, ROLES)) {
      setValue('roleId', '');
    }
  }, [allowedProfiles, domainType, hasError, isPlantAdmin, t, getValues, setValue]);

  const roleDropdownValues = filteredRoles.map(r => ({ ...r, name: t(r.name) }));
  const { systemOfMeasurementOptions } = useSystemOfMeasurementOptions();

  const handleClose = () => {
    reset({ logins: [], roleId: '', userPlants: undefined });
    clearFilter();
    setDomainValidationMessage('');
    setMultipleDomainError('');
    onClose();
  };

  const onSubmit: SubmitHandler<IFormInput> = async data => {
    // Use getValues() directly to avoid empty data parameter issue
    const formValues = getValues();
    const { logins, roleId, userPlants: selectedPlantsInForm, measurementSystem } = formValues;

    const userPlantsValue = getUserPlantsValue(selectedPlantsInForm, allPlants, isPlantAdmin ?? false, userInfo?.selectedPlant.id);

    const userToSend: UsersCreateRequest = {
      logins,
      userProfileId: Number(roleId) || 0,
      plants: userPlantsValue,
      measurementSystem: measurementSystem
    };

    onConfirm(userToSend);
  };

  const [loginsTooltipIsVisible, setLoginsTooltipIsVisible] = useState(false);

  return (
    <Dialog
      open={open}
      aria-labelledby="enhanced-invite-dialog-title"
      aria-describedby="enhanced-invite-dialog-description"
      fullWidth
      maxWidth="xs"
      style={{ overflow: 'auto' }}
    >
        <DialogTitle id="enhanced-invite-dialog-title">{t('invite-user')}</DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              paddingTop: 0,
              paddingBottom: 0,
              overflow: 'initial'
            }}
          >
            <Box>
              <Controller
                control={control}
                name="logins"
                render={({ field }) => (
                  <FormAutocompleteWithTags
                    id="logins"
                    fullWidth
                    label={t("e-mail")}
                    multiple
                    options={field.value}
                    freeSolo
                    onInputChange={(_, value) => {
                      setLoginsTooltipIsVisible(value !== '');
                    }}
                    {...field}
                    onChange={(
                      _,
                      value: string | string[] | null,
                      reason: AutocompleteChangeReason,
                    ) => {
                      const result = handleLoginsChange(value, reason);

                      if (result.action === 'clear') {
                        setValue("logins", result.newValue!);
                        field.onBlur();
                        clearFilter();
                      } else if (result.action === 'update') {
                        setValue("logins", result.newValue!);
                        field.onBlur();
                      }
                    }}
                    handleCheckboxChange={(option) => {
                      setValue("logins", [...logins].filter(item => item !== option));
                      field.onBlur();
                    }}
                  />
                )}
              />
            </Box>
            {loginsTooltipIsVisible && (
              <WarningMessageComponent message={t("invite-user.logins-tooltip")} />
            )}

            {/* Domain validation loading indicator */}
            {logins.length > 0 && isLoading && (
              <Box display="flex" alignItems="center" style={{ gap: '8px' }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="textSecondary">
                  {t('domain.validation.checking', 'Validating domain...')}
                </Typography>
              </Box>
            )}

            {/* Multiple domain error message */}
            {multipleDomainError && (
              <WarningMessageComponent message={multipleDomainError} />
            )}

            {/* Domain validation message */}
            {domainValidationMessage && !multipleDomainError && (
              <WarningMessageComponent message={domainValidationMessage} />
            )}

            <Box>
              <Controller
                name="roleId"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    id="user-invite-role-select"
                    label={t('user.profile')}
                    options={roleDropdownValues}
                    inputProps={{ ...field }}
                    error={!!errors.roleId?.message}
                    errorMessage={errors.roleId?.message}
                    disabled={isLoading || (logins.length > 0 && filteredRoles.length === 0)}
                    onChange={(e) => {
                      const roleValue = String(e.target.value);

                      // Update the roleId field value
                      field.onChange(roleValue);

                      // Calculate expected userPlants value based on new role
                      // Pass current userPlants to preserve selection when staying in same role type
                      const currentUserPlants = getValues('userPlants');
                      const newUserPlants = calculateUserPlantsOnRoleChange(
                        roleValue,
                        isPlantAdmin ?? false,
                        userInfo?.selectedPlant.id,
                        currentUserPlants
                      );

                      // Always update userPlants with the calculated value (which preserves selection when appropriate)
                      setValue('userPlants', newUserPlants ?? '');

                      // useEffect will normalize the type (string ↔ array) if needed
                    }}
                  />
                )}
              />
            </Box>
            <Box>
              <Controller
                name="measurementSystem"
                control={control}
                render={({ field }) => (
                  <FormSelect
                    fullWidth
                    id='measurement-system'
                    label={t('user.profile.select.measurement-system.label')}
                    error={!!errors.measurementSystem?.message}
                    inputProps={{...field}}
                    options={systemOfMeasurementOptions}
                    errorMessage={errors.measurementSystem?.message}
                  />
                )}
              />
            </Box>
            <Box>
              {!isPlantAdmin &&
                <>
                  {
                    watchRoleId === '' || multipleDomainError ?
                      (
                        <FormSelect
                          disabled
                          id="user-invite-plant-select-disabled"
                          label={t('admin-panel.plant')}
                          options={[]}
                        />
                      )
                      :
                      <Controller
                        name="userPlants"
                        control={control}
                        render={({ field }) => (
                          <>
                            {
                              isMultiPlantRole(watchRoleId) ?
                                (
                                  <FormAutocompleteWithTags
                                    multiple
                                    label={t('admin-panel.plant')}
                                    id="checkboxes-tags-autocomplete"
                                    options={options.map(item => item.name)}
                                    disableCloseOnSelect
                                    limitTags={3}
                                    {...field}
                                    value={field.value as string[]}
                                    onChange={(
                                      _,
                                      value: string[],
                                    ) => {
                                      setValue("userPlants",value || [])
                                    }}
                                    handleCheckboxChange={
                                      ( option: string, checked: boolean) => {
                                        const newPlants = togglePlantSelection(
                                          userPlants as string[],
                                          option,
                                          checked
                                        );
                                        setValue("userPlants", newPlants);
                                      }
                                    }
                                  />
                                )
                                :
                                (
                                  <FormSelect
                                    id="user-invite-plant-select"
                                    label={t('admin-panel.plant')}
                                    options={options}
                                    required
                                    {...field}
                                    value={field.value || ''}
                                  />
                                )
                            }
                          </>
                        )}
                      />
                  }
                </>
              }
            </Box>
          </DialogContent>

          <DialogActions>
            <FormButton onClick={handleClose} variant="secondary">
              {t('button.cancel')}
            </FormButton>
            <FormButton
              type="submit"
              variant="primary"
              disabled={!(formIsValid && isDirty) || isLoading || (logins.length > 0 && !isValid) || !!multipleDomainError}
            >
              {t('admin-panel.invite')}
            </FormButton>
          </DialogActions>
        </form>
      </Dialog>
  );
};

export default EnhancedInviteUserModal;
